import { PieceType } from '../../pieces/types';

import { ApplicationState } from '../../../types';

import { getPieceValue, executePiece } from '../index';

import { ExecutePieceReturnType, getNextPieceIdForComputedField } from './index';
import { DefaultFlowchartProcessState } from '../../types';
import { VariableValueType } from '../../../../helpers/common-types';
import { CustomField, FieldType, getValidCustomFieldValueForInput } from '../../../custom-fields/types';

export function getGroupCustomFieldPieceValue(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, groupId: string, customField: CustomField, pieceId: string): VariableValueType {
    const piecesData = applicationState.flowchart.pieces;
    const piece = piecesData.byId[pieceId];
    const groupsData = applicationState.groups;

    const functionShortHand = getGroupCustomFieldPieceValue.bind({}, applicationState, processState, groupId, customField);

    switch (piece.type) {
        case PieceType.CUSTOM_FIELD:
            if (!piece.customField) {
                throw new Error('A custom field needs to be selected');
            }

            if (piece.customFieldOption) {
                return groupsData.types.customFieldOptions.byId[piece.customFieldOption].name;
            }

            const customField = groupsData.types.customFields.byId[piece.customField];

            if (customField.isComputed) {
                let startPiece: string | undefined;

                if (customField.startPiece) {
                    startPiece = customField.startPiece.piece;
                }

                if (typeof startPiece === 'undefined') {
                    return undefined;
                }

                const computedValue = getGroupCustomFieldPieceValue(applicationState, processState, groupId, customField, startPiece);

                return computedValue;
            }

            let customFieldValue = processState.customFields[customField.id];

            if (!Array.isArray(customFieldValue) && typeof customFieldValue === 'object') {
                throw new Error('This field must be not be a collection of data for individual members');
            }

            if (customField.type === FieldType.SINGLE_SELECT) {
                if (!!customFieldValue) {
                    if (typeof customFieldValue !== 'string') {
                        throw new Error('A single select must have a custom field type');
                    }

                    customFieldValue = groupsData.types.customFieldOptions.byId[customFieldValue].name;

                }
            }

            if (customField.type === FieldType.MULTI_SELECT) {
                if (!!customFieldValue) {
                    if (!Array.isArray(customFieldValue)) {
                        throw new Error('A multi select must have an array');
                    }

                    customFieldValue = customFieldValue.map(customFieldOptionId => groupsData.types.customFieldOptions.byId[customFieldOptionId].name);

                }
            }

            return customFieldValue;

        default:
            return getPieceValue(applicationState, processState, pieceId, functionShortHand);
    }
}

// If it returns false, stop execution and display what needs to be displayed. Otherwise, feel free to get the next piece and continue executing
function executePieceForGroupComputedField(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, pieceId: string, groupId: string, customField: CustomField): ExecutePieceReturnType {
    const piecesData = applicationState.flowchart.pieces;
    const piece = piecesData.byId[pieceId];

    const getPieceShorthand = getGroupCustomFieldPieceValue.bind({}, applicationState, processState, groupId, customField);

    switch (piece.type) {

        case PieceType.RETURN:

            if (typeof piece.returnValue === 'undefined') {
                throw new Error('The return piece must have a variable')
            }

            const returnVariablePiece = piecesData.byId[piece.returnValue];
            let returnVariableValue = getPieceShorthand(returnVariablePiece.id);

            return {
                canContinueExecution: false,
                returnValue: returnVariableValue,
            };

        default:
            const canContinueExecution = executePiece(applicationState, processState, pieceId, getPieceShorthand);

            return {
                canContinueExecution,
                returnValue: undefined,
            };
    }
}

export function getGroupComputedFieldValue(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, startPiece: string | undefined, groupId: string, customField: CustomField) {
    let executionResult: ExecutePieceReturnType;
    const getPieceShorthand = getGroupCustomFieldPieceValue.bind({}, applicationState, processState, groupId, customField);

    do {
        const nextPieceId = getNextPieceIdForComputedField(applicationState, processState, startPiece, getPieceShorthand);

        if (typeof nextPieceId === 'undefined') {
            throw new Error('The next piece does not exist');
        }

        executionResult = executePieceForGroupComputedField(applicationState, processState, nextPieceId, groupId, customField);
    } while (executionResult.canContinueExecution);

    const returnValue = executionResult.returnValue;
    return getValidCustomFieldValueForInput(customField, returnValue, applicationState.groups.types.customFieldOptions);
}